'use strict';

import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { clearAuth0Cache, getTokenFromLocalStorage } from '@/utils/auth0Token.js';

const PORTAL_API_AUDIENCE = 'https://portal-api';

function isUnrecoverableAuthError(error) {
	const message = error?.message || '';
	const code = error?.error || '';
	return (
		code === 'login_required'
		|| message.includes('login_required')
		|| message.includes('Login required')
		|| message.includes('Missing Refresh Token')
	);
}

/**
 * When Auth0 still has a session but localStorage has no valid access token,
 * attempt a silent refresh. On unrecoverable failure, clear stale Auth0 cache
 * so quote builder UI (gated on useIsAuthenticated) treats the user as a guest.
 */
export function useStudyBuilderAuthSession() {
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
	const attemptedRef = useRef(false);

	useEffect(() => {
		if (isLoading || attemptedRef.current) {
			return undefined;
		}
		if (!isAuthenticated) {
			return undefined;
		}
		if (getTokenFromLocalStorage()) {
			return undefined;
		}

		attemptedRef.current = true;
		let cancelled = false;

		(async () => {
			try {
				await getAccessTokenSilently({ audience: PORTAL_API_AUDIENCE });
			} catch (error) {
				if (cancelled) {
					return;
				}
				if (isUnrecoverableAuthError(error)) {
					clearAuth0Cache();
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [isAuthenticated, isLoading, getAccessTokenSilently]);
}
